import { useState, useCallback, useMemo } from "react";
import moment from "moment-timezone";
import type {
  Action,
  CalendarAddDisplayOptions,
  SelectableOption,
} from "@core/features/actions";
import {
  useFetchActions,
  ACTION_TYPE_SLUG,
} from "@core/features/actions";
import {
  useGetSchedulingAvailability,
  getSelectedRepeatingOptionsFromSlots,
} from "@core/features/scheduling";
import { useUserMeetings } from "@platform/hooks/useUserMeetings";
import { useCurrentMeeting } from "@platform/hooks/useCurrentMeeting";
import { useUserTimezoneOption } from "@platform/hooks/useUserTimezoneOption";
import { usePlatformStore } from "@platform/store";
import { ProgramCalendarEventList } from "./ProgramCalendarEventList";

export type Props = {
  /** The calendar_add action */
  calendarAddAction: Action;
};

/** Action type slugs that are bound to meetings */
const MEETING_BOUND_SLUGS = new Set([
  ACTION_TYPE_SLUG.MEETING_SURVEY,
  ACTION_TYPE_SLUG.MEETING_READING,
  ACTION_TYPE_SLUG.MEETING_SCRIPT,
]);

/**
 * Process selectableOptions: generate IDs and compute day from date
 */
function processSelectableOptions(selectableOptions: SelectableOption[]) {
  return selectableOptions.map((opt, index) => ({
    ...opt,
    id: opt.id || `opt-${opt.date}-${opt.startHour}-${opt.startMinute}-${index}`,
    day: opt.day ?? moment.utc(opt.date).day(),
  }));
}

/**
 * Container for ProgramCalendarEventList.
 * Handles data fetching and determines whether to show meetings or scheduled times.
 */
export function ProgramCalendarEventListContainer({ calendarAddAction }: Props) {
  const user = usePlatformStore((s) => s.user);
  const isVideoDisabled = usePlatformStore((s) => s.featureFlags.isVideoDisabled);
  const [expandedMeetingId, setExpandedMeetingId] = useState<number | null>(null);
  const [hasUserToggled, setHasUserToggled] = useState(false);

  // Get user's meetings and current active meeting
  const { meetings } = useUserMeetings();
  const { currentMeeting } = useCurrentMeeting();

  // Get user's timezone
  const userTimezoneOption = useUserTimezoneOption();

  // Fetch actions for this program to find meeting-bound actions
  const { data: actionsData } = useFetchActions({
    programId: calendarAddAction.program,
    user,
    isEnabled: !!user,
  });

  // Group meeting-bound actions by binding_object_id (meeting ID)
  // Also extract MEETING actions as the data source for each meeting card
  const { actionsByMeetingId, meetingActionByMeetingId } = useMemo(() => {
    if (!actionsData?.actions) return { actionsByMeetingId: {}, meetingActionByMeetingId: {} };
    const grouped: Record<number, Action[]> = {};
    const meetingActions: Record<number, Action> = {};

    for (const action of actionsData.actions) {
      const slug = action.action_type?.slug;
      if (!slug) continue;

      const meetingId = action.binding_object_id;
      if (!meetingId) continue;

      if (slug === ACTION_TYPE_SLUG.MEETING) {
        meetingActions[meetingId] = action;
      } else if (MEETING_BOUND_SLUGS.has(slug)) {
        if (!grouped[meetingId]) grouped[meetingId] = [];
        grouped[meetingId].push(action);
      }
    }

    return { actionsByMeetingId: grouped, meetingActionByMeetingId: meetingActions };
  }, [actionsData?.actions]);

  // Extract selectableOptions, eventTitle, and eventDescription from action's display_options
  const { selectableOptions, eventTitle, eventDescription } = useMemo(() => {
    const displayOptions =
      calendarAddAction.display_options as CalendarAddDisplayOptions | null;

    return {
      selectableOptions: displayOptions?.selectableOptions
        ? processSelectableOptions(displayOptions.selectableOptions)
        : undefined,
      eventTitle:
        displayOptions?.eventTitle ||
        calendarAddAction.name ||
        "Peer Circle Meeting",
      eventDescription: displayOptions?.eventDescription,
    };
  }, [calendarAddAction]);

  // Fetch user's saved scheduling availability
  const { data: savedTimeSlots } = useGetSchedulingAvailability(
    calendarAddAction.program
  );

  // Determine which selectableOptions are selected based on saved slots
  const selectedOptions = useMemo(() => {
    if (!selectableOptions || !savedTimeSlots?.length) {
      return undefined;
    }
    const selected = getSelectedRepeatingOptionsFromSlots(
      selectableOptions,
      savedTimeSlots
    );
    return selected.length > 0 ? selected : undefined;
  }, [selectableOptions, savedTimeSlots]);

  // Handler for adding calendar events
  const handleAddCalendarEvent = useCallback(
    (
      _eventId: number | string | "all",
      _calendarType: "google" | "ical"
    ) => {
      // Calendar event handling is done within the calendar buttons themselves
    },
    []
  );

  const handleToggleMeeting = useCallback(
    (meetingId: number) => {
      setHasUserToggled(true);
      setExpandedMeetingId((prev) => (prev === meetingId ? null : meetingId));
    },
    []
  );

  // Filter out past meetings (end_time before now)
  // When video is disabled, hide all meetings
  const visibleMeetings = useMemo(() => {
    if (isVideoDisabled || !meetings) return [];
    const now = new Date().toISOString();
    return meetings.filter((m) => !m.end_time || m.end_time >= now);
  }, [isVideoDisabled, meetings]);

  // Default-expand the current meeting until user manually toggles
  const effectiveExpandedId = hasUserToggled
    ? expandedMeetingId
    : currentMeeting?.id ?? null;

  // Determine what to render
  const hasMeetings = visibleMeetings.length > 0;
  const hasSelectedOptions = selectedOptions && selectedOptions.length > 0;

  // Return null if nothing to display
  if (!hasMeetings && !hasSelectedOptions) {
    return null;
  }

  return (
    <ProgramCalendarEventList
      meetings={hasMeetings ? visibleMeetings : undefined}
      selectableOptions={hasSelectedOptions ? selectedOptions : undefined}
      eventTitle={eventTitle}
      eventDescription={eventDescription}
      userTimezone={userTimezoneOption ?? undefined}
      onAddCalendarEvent={handleAddCalendarEvent}
      currentMeetingId={currentMeeting?.id}
      expandedMeetingId={effectiveExpandedId}
      onToggleMeeting={handleToggleMeeting}
      actionsByMeetingId={actionsByMeetingId}
      meetingActionByMeetingId={meetingActionByMeetingId}
    />
  );
}

export default ProgramCalendarEventListContainer;
